import React, { useState, useEffect, useRef } from "react";
import {
  Button, Card, CardBody, CardHeader, Container,
  Modal, ModalBody, ModalFooter, ModalHeader,
  Form, FormGroup, Input, Label, Row, Col,
  Spinner, Badge, UncontrolledDropdown, DropdownItem,
  DropdownMenu, DropdownToggle, ListGroup, ListGroupItem,
  Table
} from "reactstrap";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../constants";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Edit, MoreVertical, Eye, Play, Check, Clock, Calendar, Save } from "lucide-react";
import moment from "moment";

const TaskList = () => {
  const toast = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTasks, setActiveTasks] = useState({});
  const [timers, setTimers] = useState({});
  const [dateFilter, setDateFilter] = useState("all");
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    due_date_time: "",
    priority: "medium",
    status: "not_started"
  });

  const breadcrumbItems = [
    { title: "Dashboard", link: "/" },
    { title: "Tasks", link: "#!" },
  ];

  const filterOptions = [
    { value: "all", label: "All Tasks" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "this_week", label: "This Week" },
    { value: "this_month", label: "This Month" },
    { value: "this_year", label: "This Year" },
    { value: "overdue", label: "Overdue" },
    { value: "upcoming", label: "Upcoming" }
  ];

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" }
  ];

  const statusOptions = [
    { value: "not_started", label: "Not Started" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" }
  ];

  const priorityBadge = (priority) => {
    const priorityMap = {
      low: "success",
      medium: "primary",
      high: "warning",
      urgent: "danger",
    };
    return (
      <Badge color={priorityMap[priority] || "primary"} className="text-uppercase">
        {priority}
      </Badge>
    );
  };

  const statusBadge = (status) => {
    const statusMap = {
      not_started: "secondary",
      in_progress: "info",
      completed: "success",
    };
    return (
      <Badge color={statusMap[status] || "secondary"} className="text-uppercase">
        {status.replace("_", " ")}
      </Badge>
    );
  };

  const fetchTasks = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/projects/task/my-tasks`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      setTasks(response.data.data);
      applyDateFilter(response.data.data, dateFilter);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      showToast("error", "Error", "Failed to fetch tasks");
    }
    setLoading(false);
  }, [dateFilter]);

  const applyDateFilter = (taskList, filter) => {
    const now = moment();

    switch (filter) {
      case "today":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isSame(now, 'day')
        ));
        break;
      case "yesterday":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isSame(now.clone().subtract(1, 'days'), 'day')
        ));
        break;
      case "this_week":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isSame(now, 'week')
        ));
        break;
      case "this_month":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isSame(now, 'month')
        ));
        break;
      case "this_year":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isSame(now, 'year')
        ));
        break;
      case "overdue":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isBefore(now, 'day') && task.status !== "completed"
        ));
        break;
      case "upcoming":
        setFilteredTasks(taskList.filter(task =>
          moment(task.due_date_time).isAfter(now, 'day')
        ));
        break;
      case "all":
      default:
        setFilteredTasks(taskList);
        break;
    }
  };

  const handleFilterChange = (e) => {
    const filter = e.target.value;
    setDateFilter(filter);
    applyDateFilter(tasks, filter);
  };

  const startTask = (taskId) => {
    if (activeTasks[taskId]) {
      showToast("warn", "Warning", "This task is already in progress");
      return;
    }

    setActiveTasks(prev => ({ ...prev, [taskId]: true }));

    setInterval(() => {
      setTimers(prev => ({
        ...prev,
        [taskId]: (prev[taskId] || 0) + 1
      }));
    }, 1000);

    updateTaskStatus(taskId, "in_progress");
  };

  const finishTask = (taskId) => {
    setTimers(prev => {
      const newTimers = { ...prev };
      delete newTimers[taskId];
      return newTimers;
    });

    setActiveTasks(prev => {
      const newActiveTasks = { ...prev };
      delete newActiveTasks[taskId];
      return newActiveTasks;
    });

    updateTaskStatus(taskId, "completed", timers[taskId] || 0);
  };

  const updateTaskStatus = async (taskId, status, timeTaken = null) => {
    try {
      const payload = { status };
      if (timeTaken !== null) {
        payload.total_time_taken = timeTaken;
      }

      await axios.post(`${API_BASE_URL}/projects/task/update/${taskId}`, payload, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });

      fetchTasks();
      showToast("success", "Success", "Task updated successfully");
    } catch (error) {
      console.error("Error updating task:", error);
      showToast("error", "Error", "Failed to update task");
    }
  };

  const formatDateTime = (dateTime) => {
    return dateTime ? moment(dateTime).format("MMM D, YYYY h:mm A") : "Not set";
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    let timeStr = "";
    if (hours > 0) timeStr += `${hours}h `;
    if (minutes > 0 || hours > 0) timeStr += `${minutes}m `;
    timeStr += `${secs}s`;

    return timeStr.trim();
  };

  const formatDecimalTime = (decimalHours) => {
    if (!decimalHours) return "0s";
    const totalSeconds = Math.round(decimalHours * 3600);
    return formatTime(totalSeconds);
  };

  const showToast = (severity, summary, detail) => {
    toast.current.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const openTaskView = (task) => {
    setSelectedTask(task);
    setViewModal(true);
  };

  // const openEditModal = (task) => {
  //   setSelectedTask(task);
  //   setFormData({
  //     title: task.title,
  //     description: task.description,
  //     due_date_time: task.due_date_time ? moment(task.due_date_time).format("YYYY-MM-DDTHH:mm") : "",
  //     priority: task.priority,
  //     status: task.status
  //   });
  //   setEditModal(true);
  // };

  const closeModal = () => {
    setViewModal(false);
    setEditModal(false);
    setSelectedTask(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    console.log("Form Data:", formData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/projects/task/update/${selectedTask.id}`, formData, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      showToast("success", "Success", "Task updated successfully");
      fetchTasks();
      closeModal();
    } catch (error) {
      console.error("Error updating task:", error);
      showToast("error", "Error", "Failed to update task");
    }
  };

  useEffect(() => {
    fetchTasks();

    return () => {
      Object.keys(timers).forEach(taskId => {
        clearInterval(timers[taskId]);
      });
    };
  }, [fetchTasks, timers]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="My Tasks" breadcrumbItems={breadcrumbItems} />

          {/* Filter Section */}
          <Card className="mb-4 shadow-sm">
            <CardBody className="py-3">
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label className="d-flex align-items-center text-muted">
                      <Calendar size={16} className="me-2" /> Filter by Date
                    </Label>
                    <Input
                      type="select"
                      value={dateFilter}
                      onChange={handleFilterChange}
                      className="form-select"
                    >
                      {filterOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No tasks found with the selected filter</p>
            </div>
          ) : (
            <Row>
              {filteredTasks.map((task) => (
                <Col md={6} xl={4} key={task.id} className="mb-4">
                  <Card className="h-100 task-card shadow-sm border-0">
                    <CardHeader style={{ backgroundColor: '#cfffdc' }} className="d-flex justify-content-between align-items-center py-3">
                      <h6 className="mb-0 text-truncate fw-semibold text-black">{task.title}</h6>
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="card-drop p-0 text-black">
                          <MoreVertical size={18} />
                        </DropdownToggle>
                        <DropdownMenu end className="shadow-sm border">
                          <DropdownItem onClick={() => openTaskView(task)} className="d-flex align-items-center">
                            <Eye size={14} className="me-2" /> View Details
                          </DropdownItem>
                          {/* <DropdownItem onClick={() => openEditModal(task)} className="d-flex align-items-center">
                            <Edit size={14} className="me-2" /> Edit
                          </DropdownItem> */}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </CardHeader>
                    <CardBody className="d-flex flex-column">
                      <div className="mb-3" style={{ height: '90px', overflow: 'auto' }}>
                        <p className="text-muted small mb-2">
                          <div dangerouslySetInnerHTML={{ __html: task.description || "No description provided" }} />
                          {/* {task.description || "No description provided"} */}
                        </p>
                      </div>

                      <ListGroup flush className="mb-3 flex-grow-1">
                        <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                          <span className="text-muted">Priority:</span>
                          <span>{priorityBadge(task.priority)}</span>
                        </ListGroupItem>
                        <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                          <span className="text-muted">Status:</span>
                          <span>{statusBadge(task.status)}</span>
                        </ListGroupItem>
                        <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                          <span className="text-muted">Due:</span>
                          <span className={moment(task.due_date_time).isBefore(moment()) && task.status !== "completed" ? "text-danger fw-semibold" : ""}>
                            {formatDateTime(task.due_date_time)}
                          </span>
                        </ListGroupItem>
                        {task.total_time_taken && (
                          <ListGroupItem className="px-0 py-1 d-flex justify-content-between">
                            <span className="text-muted">Time Taken:</span>
                            <span>{formatDecimalTime(task.total_time_taken)}</span>
                          </ListGroupItem>
                        )}
                      </ListGroup>

                      <div className="mt-auto">
                        {task.status === "not_started" ? (
                          <Button
                            color="primary"
                            onClick={() => startTask(task.id)}
                            className="w-100 rounded-pill"
                          >
                            <Play size={14} className="me-2" /> Start Task
                          </Button>
                        ) : task.status === "in_progress" ? (
                          <div className="d-flex align-items-center">
                            <div className="d-flex align-items-center me-2 text-primary fw-semibold">
                              <Clock size={14} className="me-1" />
                              {formatTime(timers[task.id] || 0)}
                            </div>
                            <Button
                              color="success"
                              onClick={() => finishTask(task.id)}
                              className="flex-grow-1 rounded-pill"
                            >
                              <Check size={14} className="me-2" /> Finish Task
                            </Button>
                          </div>
                        ) : (
                          <Button color="light" disabled className="w-100 rounded-pill">
                            <Check size={14} className="me-2" /> Completed
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </div>

      {/* Task View Modal */}
      <Modal isOpen={viewModal} toggle={closeModal} size="md" centered>
        <ModalHeader toggle={closeModal} className="d-flex align-items-center bg-light">
          <Eye size={18} className="me-2 text-primary" />
          <span className="fw-semibold">Task Details</span>
        </ModalHeader>
        <ModalBody>
          {selectedTask && (
            <div className="task-details">
              <div className="mb-4">
                <h5 className="mb-2 fw-semibold">{selectedTask.title}</h5>
                <div style={{ maxHeight: '100px', overflow: 'auto' }}>
                  <p className="text-muted">{selectedTask.description || "No description provided"}</p>
                </div>
              </div>

              <Table borderless className="mb-0">
                <tbody>
                  <tr>
                    <td width="30%" className="text-muted fw-semibold">Priority:</td>
                    <td>{priorityBadge(selectedTask.priority)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Status:</td>
                    <td>{statusBadge(selectedTask.status)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Due Date:</td>
                    <td className={moment(selectedTask.due_date_time).isBefore(moment()) && selectedTask.status !== "completed" ? "text-danger fw-semibold" : ""}>
                      {formatDateTime(selectedTask.due_date_time)}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted fw-semibold">Time Taken:</td>
                    <td>{formatDecimalTime(selectedTask.total_time_taken)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="secondary" onClick={closeModal} className="rounded-pill">
            Close
          </Button>
          <Button color="primary" onClick={() => {
            if (selectedTask.status === "not_started") {
              startTask(selectedTask.id);
            } else if (selectedTask.status === "in_progress") {
              finishTask(selectedTask.id);
            }
            closeModal();
          }} className="rounded-pill">
            {selectedTask?.status === "not_started" ? "Start Task" :
              selectedTask?.status === "in_progress" ? "Finish Task" : "Task Completed"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Task Edit Modal */}
      <Modal isOpen={editModal} toggle={closeModal} size="md" centered>
        <ModalHeader toggle={closeModal} className="d-flex align-items-center bg-light">
          <Edit size={18} className="me-2 text-primary" />
          <span className="fw-semibold">Edit Task</span>
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody style={{ maxHeight: '70vh', overflow: 'auto' }}>
            {selectedTask && (
              <div className="task-edit">
                <FormGroup className="mb-3">
                  <Label for="title" className="fw-semibold">Title</Label>
                  <Input
                    type="text"
                    name="title"
                    id="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label for="description" className="fw-semibold">Description</Label>
                  <Input
                    type="textarea"
                    name="description"
                    id="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label for="due_date_time" className="fw-semibold">Due Date</Label>
                  <Input
                    type="datetime-local"
                    name="due_date_time"
                    id="due_date_time"
                    value={formData.due_date_time}
                    onChange={handleInputChange}
                  />
                </FormGroup>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label for="priority" className="fw-semibold">Priority</Label>
                      <Input
                        type="select"
                        name="priority"
                        id="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        {priorityOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label for="status" className="fw-semibold">Status</Label>
                      <Input
                        type="select"
                        name="status"
                        id="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="secondary" onClick={closeModal} className="rounded-pill">
              Cancel
            </Button>
            <Button type="submit" color="primary" className="rounded-pill">
              <Save size={14} className="me-2" /> Save Changes
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Toast ref={toast} position="top-right" />
    </React.Fragment>
  );
};

export default TaskList;